import { useEffect, useState } from "react";
import { Play, ArrowUpRight, Youtube } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { api, YOUTUBE_CHANNEL } from "../../lib/api";

const fmtDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }).toUpperCase() : "";

export default function Latest() {
  const [video, setVideo] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.getLatestVideo()
      .then((d) => setVideo(d?.video || null))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="latest" className="relative py-24 md:py-36 bg-wood border-b border-woodborder" data-testid="latest-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <Eyebrow index="03" label="FRESH OFF THE CHANNEL" />
          <h2 className="font-display text-cream text-6xl md:text-8xl tracking-tight leading-[0.85]" data-testid="latest-heading">
            THE LATEST <span className="text-burnt">DROP.</span>
          </h2>
          <p className="mt-5 max-w-lg text-sand font-light">The newest episode from the official channel — surfaced automatically the moment it publishes.</p>
        </Reveal>

        {!loaded && (
          <div className="mt-14 aspect-[16/8] border border-woodborder bg-card/30 animate-pulse" data-testid="latest-skeleton" />
        )}

        {loaded && video && (
          <Reveal delay={0.12}>
            <div className="mt-14 grid lg:grid-cols-5 gap-8 items-center" data-testid="latest-video-feature">
              <a href={video.url} target="_blank" rel="noopener noreferrer" className="group relative block lg:col-span-3 border border-woodborder overflow-hidden" data-testid="latest-video-thumbnail">
                <img src={video.thumbnail} alt={video.title} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                <span className="absolute inset-0 bg-gradient-to-t from-obsidian/60 to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-20 h-20 rounded-full bg-burnt flex items-center justify-center shadow-[0_0_50px_rgba(200,90,23,0.55)] scale-90 group-hover:scale-100 transition-transform duration-500">
                    <Play size={26} className="fill-cream text-cream ml-1" />
                  </span>
                </span>
                <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] bg-burnt text-cream px-3 py-1.5">NEW EPISODE</span>
              </a>
              <div className="lg:col-span-2">
                <p className="font-mono text-[10px] tracking-[0.25em] text-gold-bright" data-testid="latest-video-date">{fmtDay(video.published)}</p>
                <h3 className="mt-4 font-editorial font-extrabold text-cream text-2xl md:text-3xl leading-tight" data-testid="latest-video-title">
                  {video.title}
                </h3>
                {video.description && (
                  <p className="mt-4 text-sand font-light leading-relaxed line-clamp-4" data-testid="latest-video-description">{video.description}</p>
                )}
                <div className="mt-8 flex flex-wrap gap-4">
                  <a href={video.url} target="_blank" rel="noopener noreferrer" data-testid="latest-watch-cta"
                    className="group flex items-center gap-3 bg-burnt hover:bg-burnt-hover text-cream font-mono text-xs tracking-[0.2em] px-7 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_36px_rgba(200,90,23,0.45)]">
                    <Play size={14} className="fill-current" /> WATCH NOW
                  </a>
                  <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" data-testid="latest-subscribe-cta"
                    className="group flex items-center gap-2 border border-tan/40 hover:border-burnt text-cream font-mono text-xs tracking-[0.2em] px-7 py-4 rounded-full transition-colors">
                    SUBSCRIBE <ArrowUpRight size={14} className="text-burnt" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        {loaded && !video && (
          <Reveal delay={0.12}>
            <div className="mt-14 border border-dashed border-tan/30 p-12 md:p-16 flex flex-col items-center text-center" data-testid="latest-fallback">
              <Youtube size={36} className="text-burnt" />
              <h3 className="mt-6 font-display text-4xl md:text-5xl text-cream tracking-wide">NEW EPISODE LOADING.</h3>
              <p className="mt-4 max-w-md text-sand font-light">The next drop is being prepped. Subscribe on YouTube so it hits you the second it lands.</p>
              <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" data-testid="latest-fallback-cta"
                className="mt-8 flex items-center gap-3 bg-burnt hover:bg-burnt-hover text-cream font-mono text-xs tracking-[0.2em] px-8 py-4 rounded-full transition-colors">
                SUBSCRIBE ON YOUTUBE <ArrowUpRight size={14} />
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
